import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Song } from '@/core/api/entities/song/song'
import { SongFeedback } from '@/core/api/entities/song/songFeedback'
import Icon from '@/shared/components/atoms/Icon'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { shouldRenderMobileDrawerMenu } from '@/shared/components/organisms/Layout/MobileDrawerMenu/MobileDrawerMenu'
import { DesktopVolumeControl } from '@/shared/components/organisms/Player/DesktopVolumeControl'
import Player from '@/shared/components/organisms/Player/Player'
import { useScrollDirection } from '@/shared/hooks/useScrollDirection'
import { cn } from '@/shared/utils/helpers'

export interface TrackPlayerProps {
	currentPlayingSong: Song
	playlist: Playlist
	filteredSongs: Song[]
	handlePreviousSong: () => void
	handleNextSong: (isAutoplay?: boolean) => void
	onPlay: () => void
	onPause: () => void
	audioElementRef: React.RefObject<HTMLAudioElement | null>
	isMuted: boolean
	setIsMuted: (isMuted: boolean) => void
	volume: number
	setVolume: (volume: number) => void
	showFeedbackIcons: boolean
	submittingFeedback: Map<number, string>
	songFeedbacks: Map<number, SongFeedback>
	isLoadingFeedback: boolean
	handleFeedbackClick: (song: Song, isLike: boolean) => void
	showCloseButton: boolean
	onClosePlayer: () => void
}
export const TrackPlayer = ({
	currentPlayingSong,
	playlist,
	filteredSongs,
	handlePreviousSong,
	handleNextSong,
	onPlay,
	onPause,
	audioElementRef,
	isMuted,
	setIsMuted,
	volume,
	setVolume,
	showFeedbackIcons,
	submittingFeedback,
	songFeedbacks,
	isLoadingFeedback,
	handleFeedbackClick,
	showCloseButton,
	onClosePlayer,
}: TrackPlayerProps) => {
	const { t } = useTranslation('common')
	const location = useLocation()
	const isMobileNavVisible = useScrollDirection({
		threshold: 8,
		topThreshold: 16,
	})
	const shouldShowMobileDrawer = shouldRenderMobileDrawerMenu(location.pathname)
	const shouldOffsetForMobileDrawer =
		shouldShowMobileDrawer && isMobileNavVisible

	return (
		<div
			className={cn(
				'fixed md:bottom-0 left-0 right-0 lg:left-20 z-[1000] transition-[bottom] duration-300 ease-out',
				shouldOffsetForMobileDrawer ? 'bottom-23' : 'bottom-0'
			)}
		>
			<div className="w-full 2xl:px-6 md:px-3">
				<div className="mx-auto w-full max-w-[1400px]">
					<div className="player-mobile-shell dark:md:bg-card md:bg-secondary md:border lg:rounded-b-[8px] md:px-6 md:py-4 lg:py-6 flex items-center lg:h-[120px] relative">
						<div className="w-full flex items-center gap-4 pr-10">
							<Player
								src={currentPlayingSong.path}
								cover={playlist?.cover}
								title={currentPlayingSong.metadata.title ?? ''}
								radioPrevText={t('entities.playlist.label')}
								radioTitle={playlist?.title || ''}
								metadata={[
									currentPlayingSong.metadata.artist ||
										t('entities.music.unknown_artist'),
									currentPlayingSong.metadata.album || '',
								].filter(Boolean)}
								disablePrevious={filteredSongs.length <= 1}
								disableNext={filteredSongs.length <= 1}
								onPrevious={handlePreviousSong}
								onNext={() => handleNextSong(false)}
								onEnded={() => handleNextSong(true)}
								autoPlay={true}
								onPlay={onPlay}
								onPause={onPause}
								hideVolume={true}
								compact={true}
								audioRef={(el) => {
									audioElementRef.current = el
									if (el) {
										el.volume = isMuted ? 0 : volume
									}
								}}
								cssVars={{
									border: 'none',
									backgroundColor: 'transparent',
								}}
							/>
							<div className="lg:flex hidden items-center gap-6 shrink-0">
								<DesktopVolumeControl
									isMuted={isMuted}
									setIsMuted={setIsMuted}
									volume={volume}
									setVolume={setVolume}
									audioElementRef={audioElementRef}
								/>
								{showFeedbackIcons && (
									<div className="flex items-center gap-2">
										{isLoadingFeedback ? (
											<>
												<Skeleton className="w-10 h-10 rounded-full" />
												<Skeleton className="w-10 h-10 rounded-full" />
											</>
										) : (
											<>
												<button
													className={cn(
														'bg-transparent border-none cursor-pointer p-1 flex items-center justify-center text-foreground opacity-70 transition-all duration-200 scale-100 hover:opacity-100 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed',
														submittingFeedback.get(currentPlayingSong.id) ===
															'like' && 'cursor-not-allowed'
													)}
													onClick={() =>
														handleFeedbackClick(currentPlayingSong, true)
													}
													disabled={
														submittingFeedback.get(currentPlayingSong.id) ===
															'like' ||
														submittingFeedback.get(currentPlayingSong.id) ===
															'dislike'
													}
												>
													{submittingFeedback.get(currentPlayingSong.id) ===
													'like' ? (
														<div className="w-5 h-5 border-2 border-[rgba(140,140,140,0.3)] border-t-[#8c8c8c] rounded-full animate-spin" />
													) : (
														(() => {
															const songFeedback = songFeedbacks.get(
																currentPlayingSong.id
															)
															const userFeedbackType =
																songFeedback?.feedbackType
															const userFeedbackTypeId =
																songFeedback?.feedbackTypeId
															const isLiked =
																((userFeedbackType &&
																	userFeedbackType.isNegative === false) ||
																	userFeedbackTypeId === 1) &&
																userFeedbackTypeId !== 2
															const likeIconColor = isLiked
																? 'var(--foreground)'
																: 'var(--muted-foreground)'
															return (
																<Icon
																	name="hand-thumbs-up"
																	width="20px"
																	height="20px"
																	color={likeIconColor}
																	hoverColor="white"
																/>
															)
														})()
													)}
												</button>
												<button
													className={cn(
														'bg-transparent border-none cursor-pointer p-1 flex items-center justify-center text-foreground opacity-70 transition-all duration-200 scale-100 hover:opacity-100 active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed',
														submittingFeedback.get(currentPlayingSong.id) ===
															'dislike' && 'cursor-not-allowed'
													)}
													onClick={() =>
														handleFeedbackClick(currentPlayingSong, false)
													}
													disabled={
														submittingFeedback.get(currentPlayingSong.id) ===
															'like' ||
														submittingFeedback.get(currentPlayingSong.id) ===
															'dislike'
													}
												>
													{submittingFeedback.get(currentPlayingSong.id) ===
													'dislike' ? (
														<div className="w-5 h-5 border-2 border-[rgba(140,140,140,0.3)] border-t-[#8c8c8c] rounded-full animate-spin" />
													) : (
														(() => {
															const songFeedback = songFeedbacks.get(
																currentPlayingSong.id
															)
															const userFeedbackType =
																songFeedback?.feedbackType
															const isDisliked =
																userFeedbackType &&
																userFeedbackType.isNegative === true
															const dislikeIconColor = isDisliked
																? 'var(--foreground)'
																: 'var(--muted-foreground)'
															return (
																<Icon
																	name="hand-thumbs-down"
																	width="20px"
																	height="20px"
																	color={dislikeIconColor}
																	hoverColor="white"
																/>
															)
														})()
													)}
												</button>
											</>
										)}
									</div>
								)}
							</div>
							{showCloseButton && (
								<button
									className="absolute top-1 right-1 md:top-2 md:right-2 rounded-[9999px] cursor-pointer p-2 flex items-center justify-center shrink-0 dark:bg-[#141414] dark:border-white/10"
									onClick={onClosePlayer}
									aria-label={t('actions.close', 'Close')}
								>
									<Icon
										name="close"
										width="16px"
										height="16px"
										color="var(--foreground)"
									/>
								</button>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
